<template>
  <component :is="as" class="cy-panel" :class="[`cy-panel--notch-${notch}`, `cy-panel--glow-${glow}`, `cy-panel--${tone}`]">
    <!-- Corner brackets sit on the two corners the notch does NOT cut
         (notch = top-left + bottom-right, brackets = top-right +
         bottom-left). Outside the clipped frame so they never get cut. -->
    <span v-if="brackets" class="cy-panel-bracket cy-panel-bracket--tr" aria-hidden="true"></span>
    <span v-if="brackets" class="cy-panel-bracket cy-panel-bracket--bl" aria-hidden="true"></span>
    <div class="cy-panel-frame">
      <div class="cy-panel-body">
        <slot />
      </div>
    </div>
  </component>
</template>

<script setup>
// HUD module primitive — notched corners, 1px rim that ignites on
// hover/focus, optional corner brackets and glow. Everything tile-like
// in the shop composes this instead of drawing bespoke frames.
// (No vue runtime APIs used — defineProps is a compiler macro, so
// there is nothing to import explicitly here.)
defineProps({
  as: { type: String, default: 'div' },
  /* 'md' = module notch (--cy-notch), 'sm' = chip notch, 'none' = square */
  notch: { type: String, default: 'md' },
  brackets: { type: Boolean, default: true },
  /* 'off' | 'idle' | 'hot' — hot is reserved for the currently-playing
     item and the primary CTA (brief §3.4). */
  glow: { type: String, default: 'off' },
  /* 'signal' (primary) | 'alarm' (accent) */
  tone: { type: String, default: 'signal' }
})
</script>

<style scoped>
.cy-panel {
  position: relative;
  /* Local tone channel — alarm swaps the rim family to the accent. */
  --cy-p-rim: var(--cy-rim);
  --cy-p-rim-hot: var(--cy-rim-hot);
  transition: filter var(--cy-t-fast) var(--cy-ease);
}
.cy-panel--alarm {
  --cy-p-rim: var(--cy-alarm-dim);
  --cy-p-rim-hot: var(--cy-alarm);
}

/* Notch size feeds the inlined clip polygons below. */
.cy-panel--notch-md { --cy-n: var(--cy-notch); }
.cy-panel--notch-sm { --cy-n: var(--cy-notch-sm); }
.cy-panel--notch-none { --cy-n: 0px; }

/* Frame trick: the outer layer paints the rim colour and clips to the
   notch; the 1px padding lets the identically-clipped body reveal it
   as a hairline that follows the cut corners. box-shadow can't do
   this — it gets clipped away with the corner. */
.cy-panel-frame {
  position: relative;
  height: 100%;
  padding: 1px;
  background: var(--cy-p-rim);
  clip-path: polygon(
    var(--cy-n) 0, 100% 0,
    100% calc(100% - var(--cy-n)), calc(100% - var(--cy-n)) 100%,
    0 100%, 0 var(--cy-n)
  );
  transition: background var(--cy-t-fast) var(--cy-ease);
}
.cy-panel-body {
  position: relative;
  height: 100%;
  background: var(--cy-module);
  clip-path: polygon(
    var(--cy-n) 0, 100% 0,
    100% calc(100% - var(--cy-n)), calc(100% - var(--cy-n)) 100%,
    0 100%, 0 var(--cy-n)
  );
}

/* Rim ignition on hover / keyboard focus within. */
.cy-panel:hover .cy-panel-frame,
.cy-panel:focus-within .cy-panel-frame {
  background: var(--cy-p-rim-hot);
}

/* Brackets: 2px thick, 12px long (brief §3.4). */
.cy-panel-bracket {
  position: absolute;
  width: 12px;
  height: 12px;
  z-index: 2;
  pointer-events: none;
  border: 0 solid var(--cy-p-rim);
  transition: border-color var(--cy-t-fast) var(--cy-ease);
}
.cy-panel-bracket--tr { top: 0; right: 0; border-top-width: 2px; border-right-width: 2px; }
.cy-panel-bracket--bl { bottom: 0; left: 0; border-bottom-width: 2px; border-left-width: 2px; }
.cy-panel:hover .cy-panel-bracket,
.cy-panel:focus-within .cy-panel-bracket,
.cy-panel--glow-hot .cy-panel-bracket {
  border-color: var(--cy-p-rim-hot);
}

/* Glow — drop-shadow (filter) instead of box-shadow because the
   clipped silhouette must carry the glow, corners included. */
.cy-panel--glow-idle { filter: drop-shadow(0 0 10px var(--cy-glow-c)); }
.cy-panel--glow-hot  { filter: drop-shadow(0 0 16px var(--cy-glow-hot-c)); }
.cy-panel--glow-hot .cy-panel-frame { background: var(--cy-p-rim-hot); }
</style>
